// src/pages/motolivraison/commerce-detail.page.jsx
//
// Détail d'un commerce avec toutes ses informations + actions admin.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Row, Col, Tag, Descriptions, Button, message, Space,
  Breadcrumb, Avatar, Spin, Tabs, Table, Empty, Typography,
  Modal, Form, Input, InputNumber, Select, Switch, Upload, Popconfirm, Image,
} from 'antd';
import {
  ShopOutlined, ArrowLeftOutlined, RightOutlined, CheckCircleOutlined,
  StopOutlined, PlayCircleOutlined, AppstoreOutlined, ShoppingCartOutlined,
  EditOutlined, PlusOutlined, DeleteOutlined, UploadOutlined, EyeInvisibleOutlined,
  EnvironmentOutlined, PhoneOutlined, MailOutlined, UserOutlined, InfoCircleOutlined,
  ClockCircleOutlined, DollarOutlined, PercentageOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motolivraisonService, citiesService } from '../../common/services';
import { formatPhoneCI } from '../../common/helper';
import dayjs from 'dayjs';
import { LOCALE_TABLE, PAGINATION_TABLE, BTN_ACTION } from './table.helpers';
import './css/motolivraison-table.css';

const { Text } = Typography;

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

// Jours de la semaine, dans l'ordre d'affichage des horaires
const JOURS = [
  { cle: 'lundi', label: 'Lundi' },
  { cle: 'mardi', label: 'Mardi' },
  { cle: 'mercredi', label: 'Mercredi' },
  { cle: 'jeudi', label: 'Jeudi' },
  { cle: 'vendredi', label: 'Vendredi' },
  { cle: 'samedi', label: 'Samedi' },
  { cle: 'dimanche', label: 'Dimanche' },
];

const statusColor = (s) => ({ pending: 'orange', approved: 'green', suspended: 'red', rejected: 'default' })[s] || 'default';
const statusLabel = (s) => ({ pending: 'En attente', approved: 'Approuvé', suspended: 'Suspendu', rejected: 'Rejeté' })[s] || s;

// Libellés + couleurs des statuts de commande
const STATUT_CMD = {
  en_attente_paiement: { c: 'orange', l: 'Attente paiement' },
  payee: { c: 'blue', l: 'Payée' },
  en_attente_livreur: { c: 'gold', l: 'Attente livreur' },
  assignee: { c: 'cyan', l: 'Assignée' },
  recuperee: { c: 'cyan', l: 'Récupérée' },
  en_livraison: { c: 'geekblue', l: 'En livraison' },
  livree: { c: 'green', l: 'Livrée' },
  confirmee: { c: 'green', l: 'Confirmée' },
  soldee: { c: 'success', l: 'Soldée' },
  annulee: { c: 'red', l: 'Annulée' },
};

// Aperçu de l'image d'un formulaire : montre l'image ACTUELLE tant qu'aucun fichier
// n'est choisi, puis l'aperçu local du nouveau fichier. Permet de voir ce qu'on
// remplace avant d'enregistrer.
const ApercuImage = ({ file, url, taille = 96 }) => {
  const [apercuLocal, setApercuLocal] = useState(null);

  useEffect(() => {
    if (!file) { setApercuLocal(null); return undefined; }
    const objectUrl = URL.createObjectURL(file);
    setApercuLocal(objectUrl);
    return () => URL.revokeObjectURL(objectUrl); // évite la fuite mémoire
  }, [file]);

  const src = apercuLocal || url;
  const style = {
    width: taille, height: taille, borderRadius: 10, objectFit: 'cover',
    border: '1px solid #f0f0f0', background: '#fafafa',
  };

  if (!src) {
    return (
      <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bfbfbf' }}>
        <ShopOutlined style={{ fontSize: 26 }} />
      </div>
    );
  }
  return (
    <div>
      <Image src={src} alt="Aperçu" width={taille} height={taille} style={style} />
      <div style={{ fontSize: 11, color: apercuLocal ? '#fa8c16' : '#8c8c8c', marginTop: 4, textAlign: 'center' }}>
        {apercuLocal ? 'Nouvelle image' : 'Image actuelle'}
      </div>
    </div>
  );
};

const CommerceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [commerce, setCommerce] = useState(null);
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [commandes, setCommandes] = useState([]);
  const [loadingCommandes, setLoadingCommandes] = useState(false);

  // Édition de la fiche commerce (admin agissant pour le commerçant)
  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [savingCommerce, setSavingCommerce] = useState(false);
  const [logoFile, setLogoFile] = useState(null);

  // Gestion du catalogue d'articles
  const [articleOpen, setArticleOpen] = useState(false);
  const [articleEdite, setArticleEdite] = useState(null); // null = création
  const [articleForm] = Form.useForm();
  const [savingArticle, setSavingArticle] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);

  // Référentiels pour les listes déroulantes
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.getCommerceById(id);
      setCommerce(data);
    } catch (e) {
      message.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const fetchArticles = useCallback(async () => {
    setLoadingArticles(true);
    try {
      const r = await motolivraisonService.getCommerceArticles(id);
      setArticles(r?.articles || []);
    } catch (e) {
      /* silencieux : l'onglet affiche simplement une liste vide */
    } finally {
      setLoadingArticles(false);
    }
  }, [id]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  // Référentiels (catégories de commerce + villes actives) pour les formulaires
  useEffect(() => {
    (async () => {
      try {
        const r = await motolivraisonService.listCategories(true);
        setCategories(r?.categories || []);
      } catch (e) { console.error('Erreur chargement catégories:', e); }
      try {
        const r = await citiesService.getAll();
        const list = Array.isArray(r) ? r : (r?.data || r?.cities || []);
        setCities(list.filter(c => c?.isActive !== false));
      } catch (e) { console.error('Erreur chargement villes:', e); }
    })();
  }, []);

  // Commandes du commerce (onglet)
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingCommandes(true);
      try {
        const r = await motolivraisonService.listCommandes({ commerceId: id, limit: 100 });
        if (alive) setCommandes(r?.commandes || []);
      } catch (e) { /* silencieux */ } finally { if (alive) setLoadingCommandes(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  const handleAction = async (action) => {
    try {
      const labels = { validate: 'approuvé', suspend: 'suspendu', reactivate: 'réactivé' };
      if (action === 'validate') await motolivraisonService.validateCommerce(id);
      else if (action === 'suspend') await motolivraisonService.suspendCommerce(id);
      else if (action === 'reactivate') await motolivraisonService.reactivateCommerce(id);
      message.success(`Commerce ${labels[action]}`);
      fetch();
    } catch (e) {
      message.error(e?.message || 'Erreur');
    }
  };

  // ─── Édition de la fiche commerce ──────────────────────────────────────
  const ouvrirEdition = () => {
    setLogoFile(null);
    editForm.setFieldsValue({
      nom: commerce.nom,
      categorieCode: commerce.categorieId?.code,
      cityCode: commerce.cityId?.code,
      adresse: commerce.adresse,
      quartier: commerce.quartier,
      telephone: commerce.telephone,
      email: commerce.email,
      description: commerce.description,
      delaiLivraisonMin: commerce.delaiLivraisonMin ?? undefined,
      delaiLivraisonMax: commerce.delaiLivraisonMax ?? undefined,
      latitude: commerce.latitude ?? undefined,
      longitude: commerce.longitude ?? undefined,
    });
    setEditOpen(true);
  };

  const enregistrerCommerce = async () => {
    try {
      const values = await editForm.validateFields();
      setSavingCommerce(true);
      // Multipart seulement si un nouveau logo est choisi, sinon JSON.
      let payload = values;
      if (logoFile) {
        const fd = new FormData();
        Object.entries(values).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') fd.append(k, v);
        });
        fd.append('image', logoFile);
        payload = fd;
      }
      await motolivraisonService.updateCommerce(id, payload);
      message.success('Fiche du commerce mise à jour');
      setEditOpen(false);
      setLogoFile(null);
      fetch();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erreur lors de la mise à jour');
    } finally {
      setSavingCommerce(false);
    }
  };

  // ─── Catalogue d'articles (saisi par l'admin pour le commerçant) ───────
  const ouvrirArticle = (article = null) => {
    setArticleEdite(article);
    setPhotoFile(null);
    articleForm.setFieldsValue({
      nom: article?.nom || '',
      prix: article?.prix ?? undefined,
      prixPromo: article?.prixPromo ?? undefined,
      isFeatured: article?.isFeatured ?? false,
      unite: article?.unite || 'pièce',
      stock: article?.stock ?? -1,
      sousCategorie: article?.sousCategorie || '',
      description: article?.description || '',
      isActive: article ? article.isActive : true,
    });
    setArticleOpen(true);
  };

  const enregistrerArticle = async () => {
    try {
      const values = await articleForm.validateFields();
      setSavingArticle(true);
      let payload = values;
      if (photoFile) {
        const fd = new FormData();
        Object.entries(values).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') fd.append(k, v);
        });
        fd.append('image', photoFile);
        payload = fd;
      }
      if (articleEdite) {
        await motolivraisonService.updateArticle(articleEdite._id, payload);
        message.success('Article modifié');
      } else {
        await motolivraisonService.createArticle(id, payload);
        message.success('Article ajouté au catalogue');
      }
      setArticleOpen(false);
      setPhotoFile(null);
      fetchArticles();
      fetch(); // les stats du commerce (nb d'articles) bougent
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || "Erreur lors de l'enregistrement de l'article");
    } finally {
      setSavingArticle(false);
    }
  };

  const basculerArticle = async (article) => {
    try {
      await motolivraisonService.toggleArticleActive(article._id);
      message.success(article.isActive ? 'Article désactivé' : 'Article activé');
      fetchArticles();
      fetch();
    } catch (e) {
      message.error(e?.message || 'Erreur');
    }
  };

  const supprimerArticle = async (article) => {
    try {
      await motolivraisonService.deleteArticle(article._id);
      message.success('Article supprimé');
      fetchArticles();
      fetch();
    } catch (e) {
      message.error(e?.message || 'Erreur lors de la suppression');
    }
  };

  // Un seul fichier, jamais d'envoi automatique : on l'attache au submit du formulaire.
  const propsUpload = (setter) => ({
    beforeUpload: (file) => { setter(file); return false; },
    onRemove: () => setter(null),
    maxCount: 1,
    accept: 'image/*',
  });

  if (loading || !commerce) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item><Link to="/dashboard/motolivraison/commerces">Commerces</Link></Breadcrumb.Item>
          <Breadcrumb.Item>{commerce.nom}</Breadcrumb.Item>
        </Breadcrumb>

        {/* ─── Bannière de la boutique : on doit sentir qu'on entre chez elle ─── */}
        <Card
          style={{ marginBottom: 16, overflow: 'hidden' }}
          styles={{ body: { padding: 0 } }}
        >
          <div
            style={{
              position: 'relative',
              padding: '22px 24px',
              background: commerce.logo
                ? `linear-gradient(135deg, rgba(51,65,85,.92), rgba(51,65,85,.75)), url(${commerce.logo}) center/cover`
                : 'linear-gradient(135deg, #334155, #475569)',
              color: '#fff',
            }}
          >
            <Row gutter={[16, 16]} align="middle" justify="space-between" wrap>
              <Col xs={24} lg={15}>
                <Space size={18} align="start" wrap={false} style={{ width: '100%' }}>
                  <Avatar
                    size={84}
                    src={commerce.logo}
                    icon={<ShopOutlined />}
                    style={{ backgroundColor: '#fa8c16', border: '3px solid rgba(255,255,255,.85)', flexShrink: 0 }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <h2 style={{ color: '#fff', margin: 0, fontSize: 24, lineHeight: 1.2 }}>{commerce.nom}</h2>
                    <Space size={[6, 6]} wrap style={{ marginTop: 8 }}>
                      <Tag color="orange" style={{ margin: 0 }}>{commerce.categorieId?.nom || 'Sans catégorie'}</Tag>
                      <Tag color={statusColor(commerce.status)} style={{ margin: 0 }}>{statusLabel(commerce.status)}</Tag>
                      <Tag color={commerce.isOpenNow ? 'green' : 'default'} style={{ margin: 0 }}>
                        {commerce.isOpenNow ? '🟢 Ouvert' : '⚫ Fermé'}
                      </Tag>
                      {!commerce.isActive && <Tag color="red" style={{ margin: 0 }}>Désactivée</Tag>}
                    </Space>
                    <div style={{ marginTop: 10, fontSize: 13, opacity: 0.9 }}>
                      <EnvironmentOutlined style={{ marginRight: 6 }} />
                      {[commerce.adresse, commerce.quartier, commerce.cityId?.name].filter(Boolean).join(' · ')}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 13, opacity: 0.9 }}>
                      <PhoneOutlined style={{ marginRight: 6 }} />
                      {formatPhoneCI(commerce.telephone)}
                      {commerce.email && <span style={{ marginLeft: 14 }}><MailOutlined style={{ marginRight: 6 }} />{commerce.email}</span>}
                    </div>
                  </div>
                </Space>
              </Col>

              <Col xs={24} lg={9} style={{ textAlign: 'right' }}>
                <Space wrap style={{ justifyContent: 'flex-end' }}>
                  <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Retour</Button>
                  <Button icon={<EditOutlined />} style={BTN_ACTION('#fa8c16')} onClick={ouvrirEdition}>
                    Modifier la fiche
                  </Button>
                  {commerce.status === 'pending' && (
                    <Button icon={<CheckCircleOutlined />} style={BTN_ACTION('#52c41a')} onClick={() => handleAction('validate')}>
                      Approuver
                    </Button>
                  )}
                  {commerce.status === 'approved' && (
                    <Button icon={<StopOutlined />} style={BTN_ACTION('#f5222d')} onClick={() => handleAction('suspend')}>
                      Suspendre
                    </Button>
                  )}
                  {commerce.status === 'suspended' && (
                    <Button icon={<PlayCircleOutlined />} style={BTN_ACTION('#52c41a')} onClick={() => handleAction('reactivate')}>
                      Réactiver
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>
          </div>

          {/* Chiffres clés de la boutique, sous la bannière */}
          <Row style={{ borderTop: '1px solid #f0f0f0' }}>
            {[
              { label: 'Articles actifs', valeur: `${fmt(commerce.stats?.articlesActifs)} / ${fmt(commerce.stats?.totalArticles)}`, couleur: '#52c41a', icone: <AppstoreOutlined /> },
              { label: 'Commandes', valeur: fmt(commerce.stats?.totalCommandes), couleur: '#1677ff', icone: <ShoppingCartOutlined /> },
              { label: 'Chiffre d\'affaires', valeur: `${fmt(commerce.stats?.revenusBrut)} F`, couleur: '#fa8c16', icone: <DollarOutlined /> },
              { label: 'Commission TIGOON', valeur: `${fmt(commerce.stats?.commissionTotale)} F`, couleur: '#722ed1', icone: <PercentageOutlined /> },
            ].map((k, i) => (
              <Col xs={12} md={6} key={k.label} style={{ padding: '14px 18px', borderLeft: i ? '1px solid #f0f0f0' : 'none' }}>
                <Space size={10} align="center">
                  <span style={{ background: `${k.couleur}1a`, color: k.couleur, width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {k.icone}
                  </span>
                  <div style={{ lineHeight: 1.25 }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{k.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: k.couleur }}>{k.valeur}</div>
                  </div>
                </Space>
              </Col>
            ))}
          </Row>
        </Card>

        <Row gutter={[16, 16]}>
          {/* ─── Colonne principale : catalogue et commandes ───────────── */}
          <Col xs={24} xl={17}>
        <Card>
          <Tabs
            defaultActiveKey="articles"
            items={[
              {
                key: 'articles',
                label: <span><AppstoreOutlined /> Articles ({articles.length})</span>,
                children: (
                  <>
                    <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Vous pouvez saisir le catalogue à la place du commerçant (carte, liste de produits…).
                      </Text>
                      <Button type="primary" icon={<PlusOutlined />} onClick={() => ouvrirArticle(null)}>
                        Ajouter un article
                      </Button>
                    </div>
                  <Table
                    className="moto-table"
                    rowKey="_id"
                    size="middle"
                    loading={loadingArticles}
                    dataSource={articles}
                    locale={{ ...LOCALE_TABLE, emptyText: <Empty description="Aucun article enregistré" /> }}
                    pagination={PAGINATION_TABLE('article')}
                    scroll={{ x: 1160 }}
                    columns={[
                      {
                        title: 'Photo', key: 'photo', width: 80, align: 'center',
                        render: (_, r) => (
                          <Avatar shape="square" size={40} src={r.photo} icon={<AppstoreOutlined />} style={{ backgroundColor: '#fff7e6', color: '#fa8c16' }} />
                        ),
                      },
                      {
                        title: 'Article', dataIndex: 'nom', key: 'nom', width: 220, ellipsis: true,
                        sorter: (a, b) => String(a.nom || '').localeCompare(String(b.nom || ''), 'fr'),
                        render: (v) => <Text strong>{v}</Text>,
                      },
                      {
                        title: 'Rayon', dataIndex: 'sousCategorie', key: 'rayon', width: 150, ellipsis: true,
                        sorter: (a, b) => String(a.sousCategorie || '').localeCompare(String(b.sousCategorie || ''), 'fr'),
                        render: (v) => v || '—',
                      },
                      {
                        title: 'Prix', dataIndex: 'prix', key: 'prix', align: 'right', width: 130,
                        sorter: (a, b) => (a.prix || 0) - (b.prix || 0),
                        render: (v, r) => (
          r.prixPromo ? (
            <div>
              <Text strong style={{ color: '#f5222d' }}>{fmt(r.prixPromo)} F</Text>
              <div>
                <Text delete type="secondary" style={{ fontSize: 11 }}>{fmt(v)} F</Text>
                {r.pourcentageReduction > 0 && (
                  <Tag color="red" style={{ marginLeft: 4, fontSize: 10, lineHeight: '14px', padding: '0 4px' }}>
                    -{r.pourcentageReduction}%
                  </Tag>
                )}
              </div>
            </div>
          ) : (
            <Text strong>{fmt(v)} F{r.unite ? <Text type="secondary" style={{ fontSize: 11 }}>/{r.unite}</Text> : ''}</Text>
          )
        ),
                      },
                      {
                        title: 'Stock', dataIndex: 'stock', key: 'stock', align: 'center', width: 100,
                        sorter: (a, b) => (a.stock ?? -1) - (b.stock ?? -1),
                        render: (v) => (v == null ? '—' : v < 0 ? 'Illimité' : fmt(v)),
                      },
                      {
                        title: 'Vendus', key: 'vendus', align: 'center', width: 100,
                        sorter: (a, b) => (a.stats?.quantiteVendue || 0) - (b.stats?.quantiteVendue || 0),
                        render: (_, r) => fmt(r.stats?.quantiteVendue),
                      },
                      {
                        title: 'Statut', key: 'st', align: 'center', width: 110,
                        filters: [{ text: 'Actif', value: true }, { text: 'Inactif', value: false }],
                        onFilter: (value, r) => r.isActive === value,
                        render: (_, r) => <Tag color={r.isActive ? 'green' : 'default'}>{r.isActive ? 'Actif' : 'Inactif'}</Tag>,
                      },
                      {
                        title: 'Actions', key: 'act', align: 'center', width: 330, fixed: 'right',
                        render: (_, r) => (
                          <Space size={6}>
                            <Button
                              size="small"
                              icon={<EditOutlined />}
                              style={BTN_ACTION('#1677ff')}
                              onClick={() => ouvrirArticle(r)}
                            >
                              Modifier
                            </Button>
                            <Button
                              size="small"
                              icon={r.isActive ? <EyeInvisibleOutlined /> : <PlayCircleOutlined />}
                              style={BTN_ACTION(r.isActive ? '#fa8c16' : '#52c41a')}
                              onClick={() => basculerArticle(r)}
                            >
                              {r.isActive ? 'Désactiver' : 'Activer'}
                            </Button>
                            <Popconfirm
                              title="Supprimer cet article ?"
                              description="Les commandes déjà passées ne sont pas affectées."
                              okText="Supprimer"
                              cancelText="Annuler"
                              okButtonProps={{ danger: true }}
                              onConfirm={() => supprimerArticle(r)}
                            >
                              <Button size="small" icon={<DeleteOutlined />} style={BTN_ACTION('#f5222d')}>
                                Supprimer
                              </Button>
                            </Popconfirm>
                          </Space>
                        ),
                      },
                    ]}
                  />
                  </>
                ),
              },
              {
                key: 'commandes',
                label: <span><ShoppingCartOutlined /> Commandes ({commandes.length})</span>,
                children: (
                  <Table
                    className="moto-table"
                    rowKey="_id"
                    size="middle"
                    loading={loadingCommandes}
                    dataSource={commandes}
                    locale={{ ...LOCALE_TABLE, emptyText: <Empty description="Aucune commande" /> }}
                    pagination={PAGINATION_TABLE('commande')}
                    scroll={{ x: 1000 }}
                    columns={[
                      { title: 'Réf', key: 'ref', width: 110, render: (_, r) => <Text code>#{String(r._id).slice(-6).toUpperCase()}</Text> },
                      {
                        title: 'Client', key: 'cli', width: 200, ellipsis: true,
                        sorter: (a, b) => String(a.clientNom || '').localeCompare(String(b.clientNom || ''), 'fr'),
                        render: (_, r) => r.clientNom
                          || (r.clientId ? `${r.clientId.firstName || ''} ${r.clientId.lastName || ''}`.trim() : '')
                          || '—',
                      },
                      {
                        title: 'Téléphone', key: 'tel', width: 150,
                        render: (_, r) => formatPhoneCI(r.clientPhone || r.clientId?.phone),
                      },
                      {
                        title: 'Mode', dataIndex: 'mode', key: 'mode', width: 120,
                        filters: [{ text: 'Téléphone', value: 'telephone' }, { text: 'App', value: 'app' }],
                        onFilter: (value, r) => r.mode === value,
                        render: (v) => <Tag>{v === 'telephone' ? 'Téléphone' : 'App'}</Tag>,
                      },
                      {
                        title: 'Montant', dataIndex: 'montantTTC', key: 'mt', align: 'right', width: 130,
                        sorter: (a, b) => (a.montantTTC || 0) - (b.montantTTC || 0),
                        render: (v) => <Text strong>{fmt(v)} F</Text>,
                      },
                      {
                        title: 'Statut', dataIndex: 'statut', key: 'st', width: 150,
                        filters: Object.entries(STATUT_CMD).map(([value, o]) => ({ text: o.l, value })),
                        onFilter: (value, r) => r.statut === value,
                        render: (v) => <Tag color={STATUT_CMD[v]?.c || 'default'}>{STATUT_CMD[v]?.l || v}</Tag>,
                      },
                      {
                        title: 'Date', dataIndex: 'createdAt', key: 'dt', width: 160,
                        defaultSortOrder: 'descend',
                        sorter: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
                        render: (v) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—',
                      },
                    ]}
                  />
                ),
              },
            ]}
          />
        </Card>
          </Col>

          {/* ─── Colonne latérale : l'identité de la boutique ──────────── */}
          <Col xs={24} xl={7}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              {commerce.ownerId && (
                <Card size="small" title={<Space><UserOutlined />Propriétaire</Space>}>
                  <Space align="start" size={12}>
                    <Avatar size={44} icon={<UserOutlined />} style={{ backgroundColor: '#fa8c16' }} />
                    <div>
                      <div style={{ fontWeight: 600 }}>
                        {`${commerce.ownerId.firstName || ''} ${commerce.ownerId.lastName || ''}`.trim() || '—'}
                      </div>
                      <div style={{ fontSize: 13, color: '#595959' }}>{formatPhoneCI(commerce.ownerId.phone)}</div>
                      {commerce.ownerId.email && (
                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>{commerce.ownerId.email}</div>
                      )}
                      <Button
                        type="link" size="small" style={{ paddingLeft: 0, marginTop: 4 }}
                        onClick={() => navigate(`/dashboard/motolivraison/commercants/${commerce.ownerId._id}`)}
                      >
                        Voir sa fiche commerçant →
                      </Button>
                    </div>
                  </Space>
                </Card>
              )}

              <Card size="small" title={<Space><EnvironmentOutlined />Localisation</Space>}>
                <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', width: 90 }}>
                  <Descriptions.Item label="Adresse">{commerce.adresse || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Quartier">{commerce.quartier || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Ville">{commerce.cityId?.name || '—'}</Descriptions.Item>
                </Descriptions>
                {commerce.latitude && commerce.longitude ? (
                  <>
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8 }}>
                      GPS : {commerce.latitude.toFixed(5)}, {commerce.longitude.toFixed(5)}
                    </div>
                    <Button
                      block icon={<EnvironmentOutlined />}
                      href={`https://www.google.com/maps/search/?api=1&query=${commerce.latitude},${commerce.longitude}`}
                      target="_blank" rel="noreferrer"
                    >
                      Ouvrir dans Google Maps
                    </Button>
                  </>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Aucune position GPS enregistrée — le livreur n'a que l'adresse texte.
                  </Text>
                )}
              </Card>

              <Card size="small" title={<Space><ClockCircleOutlined />Horaires</Space>}>
                {JOURS.some(j => commerce.horaires?.[j.cle]) ? (
                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    {JOURS.map(j => {
                      const h = commerce.horaires?.[j.cle] || {};
                      const ouvert = h.ouvert !== false && (h.ouverture || h.fermeture);
                      return (
                        <div key={j.cle} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                          <span style={{ color: '#595959' }}>{j.label}</span>
                          <span style={{ color: ouvert ? '#262626' : '#bfbfbf' }}>
                            {ouvert ? `${h.ouverture || '—'} – ${h.fermeture || '—'}` : 'Fermé'}
                          </span>
                        </div>
                      );
                    })}
                  </Space>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>Aucun horaire renseigné.</Text>
                )}
              </Card>

              <Card size="small" title={<Space><InfoCircleOutlined />Informations</Space>}>
                <Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', width: 110 }}>
                  <Descriptions.Item label="Créée le">
                    {commerce.createdAt ? dayjs(commerce.createdAt).format('DD/MM/YYYY') : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Approuvée le">
                    {commerce.approvedAt ? dayjs(commerce.approvedAt).format('DD/MM/YYYY') : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Commission">
                    {commerce.commissionMode
                      ? `${commerce.commissionValue}${commerce.commissionMode === 'percentage' ? ' %' : ' F'} (propre à la boutique)`
                      : 'Barème de la catégorie / global'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Livraisons">
                    {fmt(commerce.stats?.commandesLivrees)} livrées · {fmt(commerce.stats?.commandesAnnulees)} annulées
                  </Descriptions.Item>
                </Descriptions>
                {commerce.description && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #f0f0f0' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>{commerce.description}</Text>
                  </div>
                )}
                {commerce.rejectionReason && (
                  <div style={{ marginTop: 8, color: '#f5222d', fontSize: 12 }}>
                    Motif du rejet : {commerce.rejectionReason}
                  </div>
                )}
              </Card>
            </Space>
          </Col>
        </Row>
      </div>

      {/* ─── Modification de la fiche commerce ─────────────────────────── */}
      <Modal
        title={<span><EditOutlined style={{ marginRight: 8 }} />Modifier la fiche du commerce</span>}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={enregistrerCommerce}
        okText="Enregistrer"
        cancelText="Annuler"
        confirmLoading={savingCommerce}
        width={720}
      >
        <Form form={editForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="nom" label="Nom de la boutique" rules={[{ required: true, message: 'Nom requis' }]}>
                <Input placeholder="Ex : Boutique chez Aminata" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="categorieCode" label="Catégorie" rules={[{ required: true, message: 'Catégorie requise' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Choisir la catégorie"
                  options={categories.map(c => ({ value: c.code, label: c.nom }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="cityCode" label="Ville" rules={[{ required: true, message: 'Ville requise' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Choisir la ville"
                  options={cities.map(c => ({ value: c.code, label: c.name }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="quartier" label="Quartier">
                <Input placeholder="Ex : Bardot" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="adresse" label="Adresse" rules={[{ required: true, message: 'Adresse requise' }]}>
            <Input placeholder="Ex : Quartier Bardot, près du marché central" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="telephone" label="Téléphone de la boutique" rules={[{ required: true, message: 'Téléphone requis' }]}>
                <Input placeholder="07 00 00 00 00" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="email" label="Email (optionnel)">
                <Input type="email" placeholder="boutique@exemple.com" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description (optionnel)">
            <Input.TextArea rows={2} placeholder="Quelques mots sur la boutique…" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="delaiLivraisonMin"
                label="Délai de livraison min. (minutes)"
                extra="Affiché au client sous la forme « 35-45 min »."
              >
                <InputNumber style={{ width: '100%' }} min={0} step={5} placeholder="35" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="delaiLivraisonMax"
                label="Délai de livraison max. (minutes)"
                dependencies={['delaiLivraisonMin']}
                rules={[({ getFieldValue }) => ({
                  validator(_, value) {
                    const min = getFieldValue('delaiLivraisonMin');
                    if (value == null || min == null) return Promise.resolve();
                    return Number(value) >= Number(min)
                      ? Promise.resolve()
                      : Promise.reject(new Error('Doit être supérieur au délai minimum'));
                  },
                })]}
              >
                <InputNumber style={{ width: '100%' }} min={0} step={5} placeholder="45" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="latitude" label="Latitude (GPS)" extra="Aide le livreur à trouver la boutique">
                <InputNumber style={{ width: '100%' }} step={0.000001} placeholder="4.7485" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="longitude" label="Longitude (GPS)">
                <InputNumber style={{ width: '100%' }} step={0.000001} placeholder="-6.6363" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Logo de la boutique">
            <Space align="start" size={16}>
              <ApercuImage file={logoFile} url={commerce.logo} />
              <div>
                <Upload {...propsUpload(setLogoFile)} showUploadList={false}>
                  <Button icon={<UploadOutlined />}>
                    {commerce.logo ? "Remplacer l'image" : 'Choisir une image'}
                  </Button>
                </Upload>
                <div style={{ marginTop: 6 }}>
                  {logoFile ? (
                    <Space size={6}>
                      <Text type="secondary" style={{ fontSize: 12 }}>{logoFile.name}</Text>
                      <Button size="small" type="link" danger onClick={() => setLogoFile(null)}>
                        Annuler le changement
                      </Button>
                    </Space>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Laissez vide pour conserver l'image actuelle.
                    </Text>
                  )}
                </div>
              </div>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ─── Ajout / modification d'un article du catalogue ─────────────── */}
      <Modal
        title={
          <span>
            {articleEdite ? <EditOutlined style={{ marginRight: 8 }} /> : <PlusOutlined style={{ marginRight: 8 }} />}
            {articleEdite ? `Modifier « ${articleEdite.nom} »` : 'Ajouter un article au catalogue'}
          </span>
        }
        open={articleOpen}
        onCancel={() => setArticleOpen(false)}
        onOk={enregistrerArticle}
        okText={articleEdite ? 'Enregistrer' : 'Ajouter'}
        cancelText="Annuler"
        confirmLoading={savingArticle}
        width={640}
      >
        <Form form={articleForm} layout="vertical">
          <Row gutter={16}>
            <Col span={14}>
              <Form.Item name="nom" label="Nom de l'article" rules={[{ required: true, message: 'Nom requis' }]}>
                <Input placeholder="Ex : Riz gras" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="prix" label="Prix (FCFA)" rules={[{ required: true, message: 'Prix requis' }]}>
                <InputNumber style={{ width: '100%' }} min={0} step={50} placeholder="1000" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="prixPromo"
                label="Prix promotionnel (optionnel)"
                extra="Doit être inférieur au prix. L'app affiche le prix barré et la réduction, et c'est ce prix qui est facturé."
                dependencies={['prix']}
                rules={[({ getFieldValue }) => ({
                  validator(_, value) {
                    if (value == null || value === '') return Promise.resolve();
                    const prix = Number(getFieldValue('prix'));
                    if (Number(value) >= prix) {
                      return Promise.reject(new Error(`Doit être inférieur au prix (${prix} F)`));
                    }
                    return Promise.resolve();
                  },
                })]}
              >
                <InputNumber style={{ width: '100%' }} min={0} step={50} placeholder="Aucune promo" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isFeatured" label="Coup de cœur" valuePropName="checked"
                extra="Mis en avant dans « Laissez-vous tenter » sur l'accueil Boutique.">
                <Switch checkedChildren="En vedette" unCheckedChildren="Normal" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="unite" label="Unité">
                <Input placeholder="pièce, kg, litre…" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="stock" label="Stock" extra="-1 = illimité">
                <InputNumber style={{ width: '100%' }} min={-1} placeholder="-1" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="sousCategorie" label="Rayon / rubrique">
                <Input placeholder="Entrées, Plats…" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description (optionnel)">
            <Input.TextArea rows={2} placeholder="Détails, composition…" />
          </Form.Item>
          <Row gutter={16} align="middle">
            <Col span={12}>
              <Form.Item label="Photo de l'article">
                <Space align="start" size={16}>
                  <ApercuImage file={photoFile} url={articleEdite?.photo} taille={80} />
                  <div>
                    <Upload {...propsUpload(setPhotoFile)} showUploadList={false}>
                      <Button icon={<UploadOutlined />}>
                        {articleEdite?.photo ? "Remplacer l'image" : 'Choisir une image'}
                      </Button>
                    </Upload>
                    {photoFile && (
                      <div style={{ marginTop: 6 }}>
                        <Button size="small" type="link" danger onClick={() => setPhotoFile(null)}>
                          Annuler le changement
                        </Button>
                      </div>
                    )}
                  </div>
                </Space>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isActive" label="Visible par les clients" valuePropName="checked">
                <Switch checkedChildren="Actif" unCheckedChildren="Inactif" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default CommerceDetail;
